'use client';

import { MessageIcon } from '@insta-club/components/icons';
import { Calendar } from '@insta-club/models';
import { cn, isBeforeNowJalaali, isSameJalaaliDate, toOrdinalPersian } from '@insta-club/utilities';
import { useEffect, useRef } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

interface Props {
    value?: Calendar;
    calendar: Calendar[];
    onChange?: (day: Calendar) => void;
}

const ProgramCalendar: React.FC<Props> = ({ value, calendar, onChange }) => {
    const swiperRef = useRef<SwiperType | null>(null);

    useEffect(() => {
        if (!swiperRef.current || !value) return;
        const swiper = swiperRef.current;
        const index = calendar.findIndex((d) => d.day === value.day);
        if (index < 0) return;
        const slideEl = swiper.slides[index];
        const swiperEl = swiper.el;
        const slideRect = slideEl.getBoundingClientRect();
        const swiperRect = swiperEl.getBoundingClientRect();
        const isVisible = slideRect.left >= swiperRect.left && slideRect.right <= swiperRect.right;
        if (!isVisible) {
            swiper.slideTo(index);
        }
    }, [value, calendar]);

    return (
        <Swiper
            spaceBetween={8}
            slidesPerView='auto'
            slidesOffsetAfter={12}
            slidesOffsetBefore={12}
            style={{ overflow: 'visible', overflowX: 'clip' }}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            className='rounded-r-2xl bg-gray-50'
        >
            {calendar.map((day) => {
                const isSelected = value?.day === day.day;
                const hasPost = calendar.find((c) => c.day === day.day)?.post;
                return (
                    <SwiperSlide
                        onClick={() => onChange?.(day)}
                        key={`day-${day.day}`}
                        style={{ width: 50 }}
                        className='my-3'
                    >
                        <div className='space-y-0.5 text-center'>
                            <div
                                className={cn('mb-0.5 block text-[10px] font-normal', {
                                    'text-primary-500': isSelected
                                })}
                            >
                                <span>روز {toOrdinalPersian(day.day)}</span>
                            </div>
                            <div
                                className={cn(
                                    'shadow-main flex cursor-pointer flex-col items-center justify-center gap-1 rounded-full border-[1.5px] border-transparent bg-white pt-6 pb-3 text-gray-700',
                                    {
                                        'bg-primary-500 text-white': isSameJalaaliDate(day.date),
                                        'bg-primary-100': isBeforeNowJalaali(day.date),
                                        // 'border-secondary-500': day.notes_count > 0,
                                        'border-primary-500': isSelected,
                                        // 'border-primary-100': isSelected && day.notes_count === 0
                                        'border-t-primary-800 border-t-4': hasPost
                                    }
                                )}
                            >
                                <span className={'block text-xs font-bold'}>
                                    {isSameJalaaliDate(day.date) ? 'امروز' : day.date.dayOfWeek}
                                </span>
                                <span className={'mt-1 mb-2 block text-xs font-medium'}>
                                    {day.date.month}/{day.date.day}
                                </span>
                            </div>
                            {day.notes_count > 0 && (
                                <div className='mx-auto flex h-[20px] w-[20px] items-center justify-center space-x-0.5 rounded-full text-sm font-medium'>
                                    <MessageIcon width={16} className='text-primary-500' />
                                </div>
                            )}
                        </div>
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
};

export default ProgramCalendar;

